import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Phone, X, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/departments", label: "Departments" },
  { to: "/doctors", label: "Doctors" },
  { to: "/blog", label: "Health Tips" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [site, setSite] = useState(null);

  useEffect(() => {
    api.get("/site-content").then(({ data }) => setSite(data)).catch(() => {});
  }, []);

  const linkCls = ({ isActive }) =>
    `relative text-sm font-medium px-3.5 py-2 rounded-full transition-all duration-200 ${
      isActive
        ? "text-red-700 bg-red-50 shadow-sm ring-1 ring-red-100"
        : "text-slate-600 hover:text-red-700 hover:bg-red-50/70"
    }`;

  const phone = site?.phone || "0341-9300560";
  const whatsapp = site?.whatsapp || "923419300560";

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/60"
    >
      <div className="container-medi flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <img src="/amc-logo.png" alt="Afzal Medical Complex" className="w-11 h-11 object-contain drop-shadow-sm group-hover:scale-[1.03] transition-transform" />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading font-semibold text-slate-900 text-base">Afzal Medical</span>
            <span className="text-[11px] tracking-widest uppercase text-red-700">Complex</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="w-10 h-10 grid place-items-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition" data-testid="header-whatsapp" aria-label="WhatsApp">
            <MessageCircle className="w-5 h-5" />
          </a>
          <a href={`tel:${phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-red-700" data-testid="header-phone">
            <Phone className="w-4 h-4" /> <span>{phone}</span>
          </a>
          <Link to="/contact" className="btn-primary text-sm !py-2.5 !px-5" data-testid="header-cta">Book Visit</Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="container-medi py-4 flex flex-col gap-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base transition ${
                    isActive ? "bg-red-50 text-red-700 font-semibold ring-1 ring-red-100" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
                data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex gap-2 mt-2">
              <a href={`tel:${phone}`} className="btn-outline flex-1 justify-center !py-2.5" data-testid="mobile-call"><Phone className="w-4 h-4" /> Call</a>
              <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 justify-center flex-1 rounded-full px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition" data-testid="mobile-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
            <Link to="/contact" onClick={() => setOpen(false)} className="btn-primary justify-center mt-2" data-testid="mobile-cta">
              Book Visit
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
